<script setup lang="ts">
export type StickerFill = 'chalk' | 'ink' | 'red' | 'yellow'
export type StickerSize = 'lg' | 'md' | 'sm'
export type StickerTilt = 1 | 2 | 3 | 4 | 5 | 6
export type StickerVariant = 'badge' | 'banner' | 'bar' | 'flame'

const props = withDefaults(defineProps<{
  dashed?: boolean
  fill?: StickerFill
  /**
   * Skip the white die-cut outer wrapper and render the colored fill
   * with a thick ink border directly. Used in surfaces where every
   * sticker should share the same dark-border + drop-shadow look as
   * the SVG shape stickers (heart, star, lightning, etc.).
   */
  flat?: boolean
  round?: boolean
  /** Optional uniform scale, applied after rotate. Number or CSS scalar (e.g. 1.5, "var(--hero-scale)", "calc(1 + 0.2vw)"). */
  scale?: number | string
  size?: StickerSize
  tilt?: StickerTilt
  variant?: StickerVariant
}>(), {
  dashed: undefined,
  fill: 'yellow',
  flat: false,
  round: undefined,
  scale: undefined,
  size: 'md',
  tilt: 1,
  variant: 'banner',
})

const isRound = computed(() => props.round ?? (props.variant === 'badge' || props.variant === 'flame'))
const isDashed = computed(() => props.dashed ?? props.variant === 'banner')

const shadow = computed(() => {
  if (props.size === 'lg') {
    return 'var(--shadow-sticker-lg)'
  }
  if (props.size === 'sm') {
    return 'var(--shadow-sticker-sm)'
  }
  return 'var(--shadow-sticker)'
})

const outerRadius = computed(() => (isRound.value ? '50%' : 'var(--sticker-radius)'))
const innerRadius = computed(() => (isRound.value ? '50%' : '4px'))
const fillColor = computed(() => `var(--color-brand-${props.fill})`)
const textColor = computed(() => (props.fill === 'ink' ? 'var(--color-brand-yellow)' : 'var(--color-brand-ink)'))

const transform = computed(() => {
  const rotate = `rotate(var(--sticker-rotate-${props.tilt}))`
  if (props.scale === undefined) {
    return rotate
  }
  const scale = typeof props.scale === 'number' ? props.scale : props.scale
  return `${rotate} scale(${scale})`
})
</script>

<template>
  <!--
    Default die-cut style: white outer padding around colored inner,
    optional dashed inner border, chunky ink offset shadow. Reads as
    a "real" peelable sticker.
  -->
  <div
    v-if="!flat"
    :style="{
      padding: 'var(--sticker-edge)',
      borderRadius: outerRadius,
      boxShadow: shadow,
      transform,
    }"
    class="inline-block bg-white"
  >
    <div
      :style="{
        background: fillColor,
        color: textColor,
        border: isDashed ? 'var(--sticker-border)' : 'none',
        borderRadius: innerRadius,
      }"
      class="flex flex-col items-center justify-center"
    >
      <slot/>
    </div>
  </div>

  <!--
    Flat style: colored fill + chunky offset shadow only. No white
    die-cut, no dark border. Pairs with the .sfg-no-stroke class on
    shape stickers so a hero composition mixing word stickers with
    shape stickers reads as one consistent set: every sticker is a
    flat-color silhouette lifted off the surface by a single hard
    drop shadow.
  -->
  <div
    v-else
    :style="{
      background: fillColor,
      color: textColor,
      borderRadius: outerRadius,
      boxShadow: '0 6px 0 rgba(0, 0, 0, 0.55)',
      transform,
    }"
    class="inline-flex flex-col items-center justify-center"
  >
    <slot/>
  </div>
</template>
